// =====================================================================================
// FILE: src/components/form/MultiSelectInline.jsx
// =====================================================================================

import React, { useState, useMemo } from "react";
import { THEME } from "../../constants/theme";
import { uniqArr } from "../../utils/categoryUtils";
import { stop } from "../../utils/statusColors";

export default function MultiSelectInline({ options, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => uniqArr(value), [value]);

  const pill = (txt) => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: 999,
    border: `1px solid ${THEME.line}`,
    background: THEME.surface2,
    fontWeight: 900,
    fontSize: 12,
    whiteSpace: "nowrap",
  });

  if (!open) {
    const text = selected.length ? selected.join(", ") : "";
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          stop(e);
          if (disabled) return;
          setOpen(true);
        }}
        style={{
          width: "100%",
          cursor: disabled ? "not-allowed" : "pointer",
          direction: "ltr",
          unicodeBidi: "plaintext",
          writingMode: "horizontal-tb",
          textAlign: "left",
          minWidth: 0,
        }}
        title={text}
      >
        {selected.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
            {selected.slice(0, 3).map((s) => (
              <span key={s} style={pill(s)}>
                {s}
              </span>
            ))}
            {selected.length > 3 ? (
              <span style={{ ...pill(`+${selected.length - 3}`), opacity: 0.85 }}>
                +{selected.length - 3}
              </span>
            ) : null}
          </div>
        ) : (
          <div style={{ opacity: 0.55 }}>Click to select…</div>
        )}
      </div>
    );
  }

  const toggle = (opt) => {
    const next = new Set(selected);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    onChange(Array.from(next));
  };

  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{ width: "100%", minWidth: 0 }}
    >
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => {
            onChange([]);
            setOpen(false);
          }}
          style={{
            border: `1px solid ${THEME.line}`,
            background: "white",
            borderRadius: 12,
            padding: "7px 10px",
            cursor: "pointer",
            fontWeight: 900,
            fontSize: 12,
            boxSizing: "border-box",
          }}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            border: `1px solid ${THEME.line}`,
            background: "white",
            borderRadius: 12,
            padding: "7px 10px",
            cursor: "pointer",
            fontWeight: 900,
            fontSize: 12,
            boxSizing: "border-box",
          }}
        >
          Done
        </button>
      </div>

      <div
        style={{
          marginTop: 8,
          maxHeight: 230,
          overflowY: "auto",
          border: `1px solid ${THEME.line}`,
          borderRadius: 12,
          padding: 10,
          background: "white",
          boxSizing: "border-box",
        }}
      >
        {options.map((opt) => {
          const checked = selected.includes(opt);
          return (
            <label
              key={opt}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                padding: "7px 6px",
                borderRadius: 10,
                cursor: "pointer",
                userSelect: "none",
                background: checked ? THEME.chip : "transparent",
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt)}
                style={{ transform: "scale(1.05)" }}
              />
              <span style={{ fontSize: 13, fontWeight: 900 }}>{opt}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
